import { useState, useRef, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useMutationSyncCustomerSubscription } from './useCustomers';

/**
 * Custom hook for managing subscription sync operations with standardized loading states
 * @param {string} customerId - The customer ID to sync
 * @param {Object} options - Configuration options
 * @returns {Object} Sync state and handlers
 */
const useSubscriptionSync = (customerId, options = {}) => {
  const {
    successDuration = 3000,
    errorDuration = 5000,
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  } = options;

  const queryClient = useQueryClient();
  const [loadingAction, setLoadingAction] = useState(null);
  const [notification, setNotification] = useState(null);
  const timeoutRef = useRef(null);

  const { mutate: syncSubscription } = useMutationSyncCustomerSubscription();

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const handleSuccess = async (message) => {
    await queryClient.invalidateQueries(['customersAccounts', customerId]);
    setNotification({
      show: true,
      success: true,
      message: message || 'Subscription synced successfully',
    });

    if (onSuccessCallback) {
      onSuccessCallback();
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, successDuration);
  };

  const handleError = (error, action) => {
    const errorMessage = error?.response?.data?.message || `Failed to ${action}`;
    setNotification({
      show: true,
      success: false,
      message: errorMessage,
    });

    if (onErrorCallback) {
      onErrorCallback(error);
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, errorDuration);
  };

  const syncCustomerSubscription = () => {
    if (loadingAction) return; // Prevent multiple actions

    setLoadingAction('SyncSubscription');
    syncSubscription(customerId, {
      onSuccess: () => {
        setLoadingAction(null);
        handleSuccess('Subscription synced successfully');
      },
      onError: (error) => {
        setLoadingAction(null);
        handleError(error, 'sync subscription');
      },
    });
  };

  const reset = () => {
    setLoadingAction(null);
    setNotification(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return {
    isLoading: loadingAction === 'SyncSubscription',
    loadingAction,
    notification,
    syncCustomerSubscription,
    reset,
  };
};

export default useSubscriptionSync;
